import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import SharedRecordingExerciseOverlay from "../components/SnapsWise/comments/SharedRecordingExerciseOverlay";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getSharedRecordingFromPayload(payload) {
  return payload?.recording || payload?.item || payload || null;
}

export default function ExerciseDeepLink() {
  const { recordingId } = useParams();
  const navigate = useNavigate();

  const [recording, setRecording] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const goToCommunity = () => {
    navigate("/community");
  };

  useEffect(() => {
    let cancelled = false;

    const fetchExercise = async () => {
      if (!recordingId) {
        setError("Missing exercise id.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const token = localStorage.getItem("token");

        const response = await fetch(
          `${process.env.REACT_APP_USER_API_URL}/presentation/shared-recording/${encodeURIComponent(recordingId)}`,
          {
            method: "GET",
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Shared exercise failed with status ${response.status}`);
        }

        const payload = await response.json();
        const nextRecording = getSharedRecordingFromPayload(payload);

        if (!nextRecording) {
          throw new Error("Shared exercise response did not contain a recording.");
        }

        if (!cancelled) {
          setRecording(nextRecording);
        }
      } catch (requestError) {
        console.error("Could not load shared exercise:", requestError);

        if (!cancelled) {
          setError("We could not load this shared exercise. It may have been unshared or the link may be wrong.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchExercise();

    return () => {
      cancelled = true;
    };
  }, [recordingId]);

  if (recording) {
    return (
      <SharedRecordingExerciseOverlay
        recording={recording}
        onBack={goToCommunity}
      />
    );
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 px-4 py-8">
      <section className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-3xl items-center justify-center">
        <div className="w-full overflow-hidden rounded-[34px] border border-blue-100 bg-white shadow-[0_34px_110px_rgba(15,23,42,0.18)] ring-1 ring-white">
          <div className="border-b border-blue-100 bg-gradient-to-br from-white via-blue-50/70 to-cyan-50/50 px-6 py-6">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-blue-600">
              Audio exercise
            </p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
              Shared exercise
            </h1>
            <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
              {recordingId ? `/exercise/${recordingId}` : "/exercise"}
            </p>
          </div>

          <div className="px-6 py-8">
            {loading ? (
              <div className="rounded-[26px] border border-blue-100 bg-blue-50 px-5 py-5 text-sm font-bold text-blue-700">
                Loading exercise…
              </div>
            ) : (
              <div
                className={cx(
                  "rounded-[26px] border px-5 py-5 text-sm font-semibold",
                  error
                    ? "border-amber-200 bg-amber-50 text-amber-800"
                    : "border-slate-200 bg-slate-50 text-slate-600",
                )}
              >
                {error || "No exercise found."}
              </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={goToCommunity}
                className="inline-flex h-12 items-center justify-center rounded-2xl bg-blue-600 px-5 text-sm font-black text-white shadow-lg shadow-blue-600/20 transition hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-100"
              >
                ← Back to community
              </button>

              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 text-sm font-black text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-4 focus:ring-blue-100"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
